import ArrowForward from "@mui/icons-material/ArrowForward";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  LinearProgress,
  Stack,
  Typography,
} from "@mui/material";
import { type FC, useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import type { Account } from "../../../generic/server/supabase-account-service";
import type { MomentCardDropData } from "../../types/moment-card-drop-data";
import { CopyMomentDialogForm } from "../actions/copy-dialog";

export const CopyDialog: FC<{
  open: boolean;
  momentExport: ({ status: "ready" } & MomentCardDropData) | { status: "loading" | "error" };
  destinationAccount: Pick<Account, "id" | "name">;
  handleClose: () => void;
  onConfirm: (data?: { name: string }) => Promise<void>;
}> = ({ open, momentExport, destinationAccount, handleClose, onConfirm }) => {
  const [buttonLoading, setButtonLoading] = useState(false);
  const { t } = useTranslation();

  const handleConfirm = useCallback(
    async (data?: { name: string }) => {
      setButtonLoading(true);
      await onConfirm(data);
      handleClose();
      setButtonLoading(false);
    },
    [setButtonLoading, onConfirm, handleClose],
  );

  return (
    <Dialog open={open} onClose={handleClose} fullWidth>
      <DialogTitle>Copy Communication</DialogTitle>
      {momentExport.status === "ready" &&
        momentExport.sourceAccount.id !== destinationAccount.id && (
          <>
            <DialogContent>
              <Box>
                {t(`Communication: "{{momentTitle}}"`, { momentTitle: momentExport.moment.title })}
              </Box>
              <Stack sx={{ mt: 2 }} direction="row" spacing={1}>
                <Typography>{momentExport.sourceAccount.name}</Typography>
                <ArrowForward />
                <Typography>{destinationAccount.name}</Typography>
              </Stack>
              <Typography sx={{ mt: 2 }}>
                Are you sure you wish to proceed with copying this Communication?
              </Typography>
            </DialogContent>
            <DialogActions>
              <Button
                data-analytics-id="moment-card-drag-and-drop-dialog-cancel"
                onClick={handleClose}
              >
                Cancel
              </Button>
              <Button
                data-analytics-id="moment-card-drag-and-drop-dialog-confirm"
                type="submit"
                variant="contained"
                color="primary"
                loading={buttonLoading}
                onClick={() => void handleConfirm().catch(console.error)}
              >
                Confirm
              </Button>
            </DialogActions>
          </>
        )}
      {momentExport.status === "ready" &&
        momentExport.sourceAccount.id === destinationAccount.id && (
          <CopyMomentDialogForm
            title={momentExport.moment.title}
            onSubmit={(x) => void handleConfirm(x).catch(console.error)}
            onClose={handleClose}
          />
        )}
      {momentExport.status === "loading" && (
        <>
          <DialogContent>
            <Box sx={{ width: "100%" }}>
              <LinearProgress />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button
              data-analytics-id="moment-card-drag-and-drop-dialog-cancel"
              onClick={handleClose}
            >
              Cancel
            </Button>
          </DialogActions>
        </>
      )}
      {momentExport.status === "error" && (
        <>
          <DialogContent>
            <Alert severity="error">Failed to export Communication.</Alert>
          </DialogContent>
          <DialogActions>
            <Button
              data-analytics-id="moment-card-drag-and-drop-dialog-cancel"
              onClick={handleClose}
            >
              Cancel
            </Button>
          </DialogActions>
        </>
      )}
    </Dialog>
  );
};
